/**
 * Per-launch theme bundle plumbing.
 *
 * Each "launch" is a self-contained content variant (different throne
 * screens, different music playlist, different cosmetic copy) that can
 * be selected at boot via the `?launch=<id>` URL param. The server,
 * Anchor program, and KotH season the launch points at are configured
 * elsewhere (deploy env, season PDA) — this provider only owns the
 * client-side assets/strings.
 *
 * Falls back to `MAIN_THEME` if the manifest fetch fails so a broken
 * launch bundle never black-screens the site.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { Brand } from "./brand";

/** Shape of `/launches/<id>/theme.json`. */
export interface LaunchTheme {
  /** Launch slug — must match the folder under `client/public/launches/`. */
  id: string;
  /** Human label, used in dev UI / window title. */
  name: string;
  /** Video sources for the throne hall screens. Absolute site paths. */
  screens: {
    throne: string;
    entrance: string;
  };
  /**
   * Optional explicit music playlist (absolute site paths). When `null`
   * (or absent), the built-in `client/src/music/tracks/*` glob playlist
   * is used. Per-launch music overrides the built-in entirely.
   */
  music: string[] | null;
  /**
   * Optional brand override consumed by the in-app connect modal. Absent on
   * most launches — the modal then falls back to the boot-time `resolveBrand()`
   * default (Crownclash). A malformed `brand` block is dropped (left
   * `undefined`) rather than failing the whole theme.
   */
  brand?: Brand;
}

/** Hardcoded fallback. Mirrors the historical defaults pre-LaunchProvider. */
export const MAIN_THEME: LaunchTheme = {
  id: "main",
  name: "Throne Hall — Default",
  screens: {
    throne: "/videos/1.webm",
    entrance: "/videos/2.webm",
  },
  music: null,
};

interface LaunchContextValue {
  theme: LaunchTheme;
  /** `true` while the initial manifest fetch is in flight. */
  loading: boolean;
  /** Set when the manifest fetch failed — components stay on MAIN_THEME. */
  error: Error | null;
}

const LaunchContext = createContext<LaunchContextValue>({
  theme: MAIN_THEME,
  loading: false,
  error: null,
});

/** Read `?launch=<id>` from the current URL, defaulting to "main". */
function readLaunchId(): string {
  if (typeof window === "undefined") return "main";
  const url = new URL(window.location.href);
  const raw = url.searchParams.get("launch");
  if (raw === null) return "main";
  // Defensive: only allow slug-safe characters so a stray param can't
  // escape the URL path (e.g., "../etc/passwd") when we build the fetch URL.
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(raw)) return "main";
  return raw;
}

interface LaunchProviderProps {
  children: ReactNode;
  /** Test seam: skip the fetch and use this theme directly. */
  overrideTheme?: LaunchTheme;
}

export function LaunchProvider({
  children,
  overrideTheme,
}: LaunchProviderProps): JSX.Element {
  const initialId = useMemo(readLaunchId, []);
  const [theme, setTheme] = useState<LaunchTheme>(
    overrideTheme ?? (initialId === "main" ? MAIN_THEME : MAIN_THEME),
  );
  const [loading, setLoading] = useState<boolean>(
    overrideTheme === undefined && initialId !== "main",
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (overrideTheme !== undefined) return;
    if (initialId === "main") return;

    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetch(`/launches/${initialId}/theme.json`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          throw new Error(`theme manifest fetch failed: HTTP ${res.status}`);
        }
        const body = (await res.json()) as unknown;
        const parsed = parseTheme(body, initialId);
        if (cancelled) return;
        setTheme(parsed);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(
          `[launch] failed to load theme "${initialId}"; falling back to MAIN_THEME`,
          err,
        );
        setTheme(MAIN_THEME);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialId, overrideTheme]);

  const value = useMemo<LaunchContextValue>(
    () => ({ theme, loading, error }),
    [theme, loading, error],
  );

  return <LaunchContext.Provider value={value}>{children}</LaunchContext.Provider>;
}

/** Read the current launch theme. Always returns a usable theme (falls back to MAIN_THEME). */
export function useLaunchTheme(): LaunchTheme {
  return useContext(LaunchContext).theme;
}

/** Full context including loading/error. Use when you need to render a spinner. */
export function useLaunchContext(): LaunchContextValue {
  return useContext(LaunchContext);
}

/**
 * Parse and validate an unknown JSON blob into a LaunchTheme. Throws on
 * structural mismatch so the caller can log + fall back.
 */
function parseTheme(raw: unknown, expectedId: string): LaunchTheme {
  if (raw === null || typeof raw !== "object") {
    throw new Error("theme manifest is not an object");
  }
  const obj = raw as Record<string, unknown>;
  const id = asString(obj["id"], "id");
  if (id !== expectedId) {
    throw new Error(`theme manifest id "${id}" does not match folder "${expectedId}"`);
  }
  const name = asString(obj["name"], "name");
  const screensRaw = obj["screens"];
  if (screensRaw === null || typeof screensRaw !== "object") {
    throw new Error("theme.screens is not an object");
  }
  const screens = screensRaw as Record<string, unknown>;
  const throne = asString(screens["throne"], "screens.throne");
  const entrance = asString(screens["entrance"], "screens.entrance");

  let music: string[] | null = null;
  const musicRaw = obj["music"];
  if (Array.isArray(musicRaw)) {
    music = musicRaw.map((v, i) => asString(v, `music[${i}]`));
  } else if (musicRaw !== null && musicRaw !== undefined) {
    throw new Error("theme.music must be an array or null");
  }

  return { id, name, screens: { throne, entrance }, music, brand: parseBrand(obj["brand"]) };
}

/**
 * Best-effort parse of the optional `brand` block. Returns `undefined` (so the
 * connect modal falls back to the boot default) for anything malformed —
 * cosmetic branding must never black-screen or fail a launch.
 */
function parseBrand(raw: unknown): Brand | undefined {
  if (raw === null || typeof raw !== "object") return undefined;
  try {
    const b = raw as Record<string, unknown>;
    const a = b["accent"];
    if (a === null || typeof a !== "object") return undefined;
    const acc = a as Record<string, unknown>;
    return {
      id: asString(b["id"], "brand.id"),
      appName: asString(b["appName"], "brand.appName"),
      tagline: asString(b["tagline"], "brand.tagline"),
      iconUrl: asString(b["iconUrl"], "brand.iconUrl"),
      url: asString(b["url"], "brand.url"),
      accent: {
        primary: asString(acc["primary"], "brand.accent.primary"),
        cta: asString(acc["cta"], "brand.accent.cta"),
        bg: asString(acc["bg"], "brand.accent.bg"),
        card: asString(acc["card"], "brand.accent.card"),
        text: asString(acc["text"], "brand.accent.text"),
        subtext: asString(acc["subtext"], "brand.accent.subtext"),
        border: asString(acc["border"], "brand.accent.border"),
      },
    };
  } catch {
    return undefined;
  }
}

function asString(v: unknown, field: string): string {
  if (typeof v !== "string" || v.length === 0) {
    throw new Error(`theme field "${field}" must be a non-empty string`);
  }
  return v;
}
